import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Infinite scroll driven by an IntersectionObserver on a sentinel element.
 *
 * Prefer this over `useInfiniteScroll` when the list can sit in more than one
 * scroll container. `useInfiniteScroll` attaches a scroll listener to a
 * specific element, which means the caller has to know which ancestor actually
 * scrolls — and on My Jobs that changes with the breakpoint (each table owns an
 * inner scroller on desktop; the page scrolls on mobile). A sentinel with
 * `root: null` intersects correctly through nested scrollers, so the same code
 * works either way.
 *
 * Usage:
 *   const { sentinelRef, isLoadingMore } = useLoadMoreOnScroll({ hasMore, loadMore });
 *   ...
 *   {hasMore && <Box ref={sentinelRef} sx={{ height: "1px" }} />}
 *
 * @param {boolean} hasMore    Whether another page exists.
 * @param {Function} loadMore  Async; fetches and appends the next page.
 * @param {boolean} isLoading  True while the FIRST page is loading.
 * @param {number} rootMargin  How far ahead of the sentinel to trigger, in px.
 */
const useLoadMoreOnScroll = ({
  hasMore,
  loadMore,
  isLoading = false,
  rootMargin = 200,
}) => {
  const sentinelRef = useRef(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // A ref as well as state: the observer callback closes over the value at
  // subscribe time, and two intersections can fire before React re-renders.
  const isFetchingRef = useRef(false);

  const handleIntersect = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoadingMore(true);
    try {
      await loadMore();
    } finally {
      isFetchingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [loadMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) handleIntersect();
      },
      { rootMargin: `${rootMargin}px` },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, handleIntersect, rootMargin]);

  return { sentinelRef, isLoadingMore };
};

export default useLoadMoreOnScroll;
